import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useSwot } from '../context/SwotContext';
import SwotItem from './SwotItem';

const { FiPlus, FiTrendingUp, FiTrendingDown, FiTarget, FiShield } = FiIcons;

const SwotQuadrant = ({ title, category, color, description }) => {
  const { [category]: items, addItem, moveItem } = useSwot();
  const [newItem, setNewItem] = useState('');
  const [priority, setPriority] = useState('medium');
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const colorConfig = {
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      header: 'bg-green-600',
      icon: FiTrendingUp,
      accent: 'text-green-600',
      dragOver: 'bg-green-100 border-green-400'
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      header: 'bg-red-600',
      icon: FiTrendingDown,
      accent: 'text-red-600',
      dragOver: 'bg-red-100 border-red-400'
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      header: 'bg-blue-600',
      icon: FiTarget,
      accent: 'text-blue-600',
      dragOver: 'bg-blue-100 border-blue-400'
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      header: 'bg-orange-600',
      icon: FiShield,
      accent: 'text-orange-600',
      dragOver: 'bg-orange-100 border-orange-400'
    }
  };

  const config = colorConfig[color];

  const handleAddItem = () => {
    if (newItem.trim()) {
      addItem(category, newItem, priority);
      setNewItem('');
      setPriority('medium');
      setIsAddingItem(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddItem();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    // Only set drag over to false if we're leaving the quadrant entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      const { itemId, sourceCategory } = dragData;
      
      if (sourceCategory !== category) {
        moveItem(itemId, sourceCategory, category);
        
        // Show a brief success indicator
        const dropZone = e.currentTarget;
        dropZone.style.transform = 'scale(1.02)';
        setTimeout(() => {
          dropZone.style.transform = 'scale(1)';
        }, 200);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };

  // Get the singular form of the title for the button
  const getSingularTitle = (title) => {
    if (title === 'Strengths') return 'Strength';
    if (title === 'Weaknesses') return 'Weakness';
    if (title === 'Opportunities') return 'Opportunity';
    if (title === 'Threats') return 'Threat';
    return title.slice(0, -1); // fallback: remove 's'
  };

  const singularTitle = getSingularTitle(title);

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`${isDragOver ? config.dragOver : config.bg} ${isDragOver ? 'border-4' : 'border-2'} ${config.border} rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 h-[500px] flex flex-col ${isDragOver ? 'scale-105' : ''}`}
    >
      {/* Header */}
      <div className={`${config.header} text-white p-6 rounded-t-2xl relative`}>
        <div className="flex items-center gap-3 mb-2">
          <SafeIcon icon={config.icon} className="w-6 h-6" />
          <h2 className="text-2xl font-bold">{title}</h2>
        </div>
        <p className="text-white/80 text-sm">{description}</p>
        
        {/* Drop indicator */}
        {isDragOver && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute top-2 right-2 bg-white/20 rounded-full p-2"
          >
            <SafeIcon icon={FiTarget} className="w-4 h-4 text-white" />
          </motion.div>
        )}
      </div>

      {/* Items List */}
      <div className="flex-1 p-6 overflow-y-auto group">
        {isDragOver && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 border-2 border-dashed border-current rounded-lg text-center text-sm font-medium opacity-60"
          >
            Drop item here to move to {title}
          </motion.div>
        )}
        
        <AnimatePresence>
          {items.map((item) => (
            <SwotItem
              key={item.id}
              item={item}
              category={category}
              color={color}
            />
          ))}
        </AnimatePresence>

        {items.length === 0 && !isDragOver && (
          <div className="text-center text-gray-400 py-8">
            <SafeIcon icon={config.icon} className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No {title.toLowerCase()} added yet</p>
            <p className="text-xs mt-1">Click "Add {singularTitle}" or drag items here</p>
          </div>
        )}

        {/* Add Item Form */}
        <AnimatePresence>
          {isAddingItem && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4"
            >
              <div className="bg-white rounded-xl p-4 border-2 border-dashed border-gray-300">
                <textarea
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={`Add a new ${singularTitle.toLowerCase()}...`}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={3}
                  autoFocus
                />
                <div className="flex items-center justify-between mt-3">
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">Low Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="high">High Priority</option>
                  </select>
                  <div className="flex gap-2">
                    <button
                      onClick={handleAddItem}
                      disabled={!newItem.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setIsAddingItem(false);
                        setNewItem('');
                        setPriority('medium');
                      }}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Add Button */}
      <div className="p-6 pt-0">
        {!isAddingItem && (
          <button
            onClick={() => setIsAddingItem(true)}
            className={`w-full py-3 border-2 border-dashed border-gray-300 rounded-xl hover:border-gray-400 transition-colors flex items-center justify-center gap-2 ${config.accent} hover:bg-white/50`}
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
            Add {singularTitle}
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default SwotQuadrant;